import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { saveAs } from 'file-saver'
import { toast } from 'react-toastify'

// UI
import { Box, Button, Flex, Heading, Spacer, Text } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'

// Component

import { authSelector } from '@/stores/auth/authSlice'
import { useConfirmationModalManagement } from '@/stores/ui/confirmationModalSlice'
import { camelToWords } from '@/utils/stringHelper'

import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'
import { createGCPRICS } from '@/utils/outlook'

//API

import useUser from '@/features/users/useUser'
import useOfferGCPR from '@/features/offers/hooks/useOfferGCPR'

//Constant
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { getOfferListCommonColumns } from '@/features/offers/constants/offerColumns'
import { SystemVariableType } from '@/features/systemVariables/constants'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import { PriceCell, RowSelectionCell } from '@/components/Table/components/Cell'
import DateCell from '@/components/Table/components/Cell/DateCell'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { createExcelFromAoA } from '@/utils/fileHelper'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { useGCKCode } from '@/features/GCKCodes/useGCKCode'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import useOfferList from '@/features/offers/hooks/useOfferList'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { ApprovalStatus } from '../../constants'
import { ApprovalInvitationStatus, GCPRStatus, PRApprovalStatus, PRStatus } from '@eapproval/offer-shared'

const OfferGCPRApprovalListPage = () => {
  const { user: currentUser } = useSelector(authSelector)
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const approvalStatus = searchParams.get('approvalStatus')
  const GCPRSubmissionStatus = searchParams.get('GCPRSubmissionStatus')
  const actionUser = searchParams.get('actionUser')

  const initialFilter = useMemo(() => {
    const columnFilters = []
    if (approvalStatus) {
      columnFilters.push({ id: 'approvalStatus', value: approvalStatus.split(',') })
    }

    if (GCPRSubmissionStatus) {
      columnFilters.push({ id: 'GCPRSubmissionStatus', value: GCPRSubmissionStatus.split(',') })
    }

    if (actionUser) {
      columnFilters.push({ id: 'actionUser', value: actionUser.split(',') })
    }

    if (columnFilters.length === 0) {
      return {}
    }

    return { columnFilters: columnFilters }
  }, [approvalStatus, actionUser, GCPRSubmissionStatus])

  // Local State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:offerPRApproval`, {})

  const query = convertTableStateToQuery(storedTableState)
  // Hooks

  const { useFetchGCPRApprovals } = useOfferList()
  const { useCostCenterOptions } = useCostCenter()
  const { useGCKCodeOptions } = useGCKCode()
  const { useUserOptions } = useUser()
  const { useSystemVariableOptions } = useSystemVariable()

  const { data: offerGCPRApprovalResponse, isRefetching } = useFetchGCPRApprovals(query)
  const downloadQuery = useMemo(() => ({ ...query, page: 1, pageSize: 0 }), [query])
  const { refetch: refetchAllGCPRApprovals } = useFetchGCPRApprovals(downloadQuery, {
    enabled: false,
    staleTime: Infinity,
  })
  const { data: allUserOptions } = useUserOptions({ status: 'active' })
  const { useBatchApproveGCPR } = useOfferGCPR()
  const batchApproveGCPR = useBatchApproveGCPR()
  const { open: openConfirmationModal } = useConfirmationModalManagement()
  const queryClient = useQueryClient()

  //Options

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const columnHelper = createColumnHelper()

  const handleGotoOffer = (row) => {
    if (!row?.original?._id) {
      return
    }
    navigate(`/offers/${row?.original?._id}?GCPRSubmissionIndex=${row?.original?.submissionIndex}`)
  }

  const checkCanSelectRow = (row) => {
    return row?.original?.approvalStatus === ApprovalStatus.REQUIRED
  }

  const selectedGCPRDetails = useMemo(() => {
    const rows = offerGCPRApprovalResponse?.data?.data
    if (!Array.isArray(rows)) {
      return []
    }
    return Object.keys(storedTableState?.rowSelection ?? {})
      .map((index) => rows[index])
      .filter(Boolean)
      .map((row) => ({
        offerId: row._id,
        submissionIndex: row.submissionIndex,
      }))
  }, [offerGCPRApprovalResponse?.data?.data, storedTableState?.rowSelection])

  const handleBatchApprove = async () => {
    if (selectedGCPRDetails.length === 0) {
      toast.error('You have not selected any valid approvals.')
      return
    }

    const { isConfirmed } = await openConfirmationModal({
      title: 'Confirm Batch Approve',
      message: `Are you sure you want to approve ${selectedGCPRDetails.length} selected GCPR submission(s)?`,
    })

    if (!isConfirmed) {
      return
    }

    try {
      dispatch(showLoadingOverlay())

      const { data: batchApprovalResult } = await batchApproveGCPR.mutateAsync(selectedGCPRDetails)
      queryClient.invalidateQueries(['offers', 'list'])

      if (Array.isArray(batchApprovalResult)) {
        const toastFn = batchApprovalResult.some((result) => !result.success) ? toast.error : toast.success
        toastFn(
          <Flex direction={'column'}>
            {batchApprovalResult.map((result, index) => (
              <Text key={index}>{`[${index + 1}/${batchApprovalResult.length}] ${result.remark}`}</Text>
            ))}
          </Flex>
        )
      }
    } catch (err) {
      console.log(err)
    } finally {
      setStoredTableState((prev) => ({ ...prev, rowSelection: {} }))
      dispatch(hideLoadingOverlay())
    }
  }

  const defaultColumns = [
    columnHelper.display({
      id: 'select',
      header: () => <></>,
      size: 50,
      cell: RowSelectionCell,
    }),
    columnHelper.display({
      id: 'action',
      size: 80,
      enableHiding: false,
      enableColumnFilter: false,
      cell: ({ row: { original } }) => {
        return (
          <Button
            size="sm"
            colorScheme="blue"
            onClick={(e) => {
              e.stopPropagation()

              try {
                createGCPRICS(
                  original.approvalDueDate,
                  original.PRNumber,
                  original.descriptionOfProduct,
                  original._id,
                  original.submissionIndex
                )
              } catch (err) {
                toast.error(err.message)
              }
            }}
          >
            Outlook
          </Button>
        )
      },
    }),
    columnHelper.accessor('PRNumber', {
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('raiser', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: allUserOptions,
      },
    }),
    columnHelper.accessor('requestor', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: allUserOptions,
      },
    }),
    columnHelper.accessor('requestDate', {
      meta: { filterVariant: FilterType.DATE },
      cell: DateCell,
    }),
    columnHelper.accessor('totalAmount', {
      header: () => 'Total Amount',
      meta: { filterVariant: FilterType.RANGE },
      cell: PriceCell,
    }),
    columnHelper.accessor('descriptionOfProduct', {
      header: () => 'Description of Product',
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('expectedDeliveryDate', {
      header: () => 'Expected Delivery Date',
      meta: { filterVariant: FilterType.DATE },
      cell: DateCell,
    }),
    columnHelper.accessor('deliveryInstructions', {
      header: () => 'Delivery Instructions',
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('PONumber', {
      header: () => 'PO#',
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('GCPRSubmissionStatus', {
      header: () => 'GCPR Status',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(GCPRStatus).map((v) => ({ label: v, value: v })),
      },
    }),
    columnHelper.accessor('approvalRole', {
      header: () => 'Approval Type',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [
          ContributorGroupType.LOGISTIC,
          ContributorGroupType.COMMERCIAL_PROJECT_MANAGER,
          ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
          ContributorGroupType.LICENSE_MANAGER,
          'GCPR Approval Delegatee',
        ].map((v) => ({ label: v, value: v })),
      },
    }),
    columnHelper.accessor('logisticType', {
      header: () => 'Logistic Type',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: ['Create Master', 'Fill PO'].map((v) => ({ label: v, value: v })),
      },
    }),
    columnHelper.accessor('actionUser', {
      header: () => 'Action User',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: allUserOptions,
      },
    }),
    columnHelper.accessor('approvalUser', {
      header: () => 'Approval User',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: allUserOptions,
      },
    }),
    columnHelper.accessor('approvalStatus', {
      header: () => 'Approval Status',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [...Object.values(PRApprovalStatus), ...Object.values(ApprovalInvitationStatus)].map((v) => ({
          label: v,
          value: v,
        })),
      },
    }),

    columnHelper.accessor('approvalDueDate', {
      header: () => 'Approval Due Date',
      meta: { filterVariant: FilterType.DATE },
      cell: DateCell,
    }),
    columnHelper.accessor('WBSNumbers', {
      header: () => 'WBS',
      enableColumnFilter: true,
      meta: { filterVariant: FilterType.TEXT },
      cell: ({ getValue }) => {
        const value = getValue()
        if (!value) return null
        return <span>{value}</span>
      },
    }),
  ]

  useEffect(() => {
    setStoredTableState((prev) => ({
      ...prev,
      rowSelection: {},
    }))
  }, [storedTableState?.pagination?.pageIndex, setStoredTableState])

  useEffect(() => {
    if (Object.keys(initialFilter).length > 0) {
      setStoredTableState((prev) => {
        return {
          ...prev,
          ...initialFilter,
        }
      })

      navigate(location.pathname, { replace: true })
    }
  }, [initialFilter, navigate, setStoredTableState])

  const handleDownload = async () => {
    try {
      dispatch(showLoadingOverlay())

      const { data: response } = await refetchAllGCPRApprovals()
      const downloadRows = response?.data?.data || []

      if (!Array.isArray(downloadRows) || downloadRows.length === 0) {
        toast.info('No data available for download.')
        return
      }

      const headers = [
        'PR Number',
        'Raiser',
        'Requestor',
        'Request Date',
        'Total Amount',
        'Description Of Product',
        'Expected Delivery Date',
        'Delivery Instructions',
        'PO#',
        'GCPR Status',
        'Approval Type',
        'Logistic Type',
        'Approver User',
        'Approval Status',
        'Approval Due Date',
        'WBS',
      ]

      const dataRows = downloadRows.map((row) => [
        row?.PRNumber ?? '',
        row?.raiser ?? '',
        row?.requestor ?? '',
        row?.requestDate ?? '',
        row?.totalAmount ?? '',
        row?.descriptionOfProduct ?? '',
        row?.expectedDeliveryDate ?? '',
        row?.deliveryInstructions ?? '',
        row?.PONumber ?? '',
        row?.GCPRSubmissionStatus ?? '',
        row?.approvalRole ?? '',
        row?.logisticType ?? '',
        row?.actionUser ?? '',
        row?.approvalStatus ?? '',
        row?.approvalDueDate ?? '',
        row?.WBSNumbers ?? '',
      ])

      const excelBlob = createExcelFromAoA([headers, ...dataRows])
      saveAs(excelBlob, 'GCPR Approvals.xlsx')
    } catch (error) {
      console.error(error)
      toast.error('Failed to download GCPR approvals.')
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  if (!currentUser?.fullName) {
    return <>Loading...</>
  }

  return (
    <>
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading>GCPR Approvals ({offerGCPRApprovalResponse?.data.count ?? 0})</Heading>
          <Spacer />

          <Flex gap={1} alignItems={'center'}>
            <Button colorScheme={'green'} onClick={handleDownload}>
              Download
            </Button>
            <Button colorScheme={'blue'} onClick={handleBatchApprove}>
              Batch Approve
            </Button>
          </Flex>
        </Flex>
        <Box p={5} overflow={'scroll'}>
          <FullTable
            data={offerGCPRApprovalResponse?.data.data ? offerGCPRApprovalResponse.data.data : []}
            rowCount={offerGCPRApprovalResponse?.data.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleGotoOffer}
            enableRowSelection={checkCanSelectRow}
            isLoading={isRefetching}
          />
        </Box>
      </Box>
    </>
  )
}

export default OfferGCPRApprovalListPage
